import { useState, useCallback, useRef, useEffect } from 'preact/hooks'
import type { ChatConfig, Message } from '../types/chat'
import { loadState, saveState, generateSessionId, loadConsent, saveConsent, clearConsent } from '../utils/storage'
import { sanitizeHtml } from '../utils/sanitize'
import { renderMarkdown } from '../utils/markdown'
import { resolvePrivacyTexts } from '../utils/privacy'
import { detectLocale, pickLocale } from '../utils/locale'
import { resolveUiTexts } from '../utils/i18n'
import defaultBotAvatar from '../assets/bot-avatar.svg'

const DEFAULTS = {
  method: 'POST' as const,
  headers: {},
  brandColor: '#1a1a1a',
  accentColor: '#000000',
  botName: 'Chatbox',
  botAvatarUrl: defaultBotAvatar,
  userAvatarUrl: '',
  launcherText: '',
  launcherVariant: 'icon' as const,
  position: 'right' as const,
  zIndex: 999999,
  openByDefault: false,
  storageKey: 'na15-n8n-brandable-chatbox',
  typingIndicatorText: 'Typing...',
  darkMode: false,
  allowHTMLInResponses: false,
  allowMarkdownInResponses: false,
  extraContext: {},
  maxMessages: 200,
  sessionTtlMinutes: 0,
}

function msgId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function resolveWelcomeMessage(locale: string, welcomeMessages?: Record<string, string>, fallback?: string): string | undefined {
  return pickLocale(locale, welcomeMessages, fallback)
}

export function useChat(userConfig: ChatConfig) {
  const config = { ...DEFAULTS, ...userConfig }
  const uiTexts = resolveUiTexts(config)

  // --- Privacy gate (DSGVO/TTDSG) ---
  // When a privacy config is present and enabled, the chat stays inert until
  // the user accepts: no localStorage access, no welcome, no webhook request.
  const privacy = config.privacy
  const consentRequired = !!privacy && privacy.enabled !== false
  const rememberConsent = privacy?.rememberConsent !== false
  const consentStorageKey = privacy?.consentStorageKey ?? `${config.storageKey}:consent`

  const [consentGranted, setConsentGranted] = useState<boolean>(() => {
    if (!consentRequired) return true
    if (!rememberConsent) return false
    return loadConsent(consentStorageKey, privacy?.consentVersion)
  })

  // Only touch persisted chat state once consent allows it.
  const storageReadable = !consentRequired || consentGranted

  // Session / history init
  const [sessionId, setSessionId] = useState<string>(() => {
    const stored = storageReadable ? loadState(config.storageKey) : null
    if (!stored) return generateSessionId()

    // TTL check
    if (config.sessionTtlMinutes && config.sessionTtlMinutes > 0) {
      const ttlMs = config.sessionTtlMinutes * 60 * 1000
      const base = Math.max(stored.lastUserAt ?? 0, stored.lastBotAt ?? 0)
      if (base && Date.now() - base > ttlMs) return generateSessionId()
    }

    return stored.sessionId ?? generateSessionId()
  })

  const [messages, setMessages] = useState<Message[]>(() => {
    const stored = storageReadable ? loadState(config.storageKey) : null
    if (!stored) return []

    // TTL check — clear history on expiry
    if (config.sessionTtlMinutes && config.sessionTtlMinutes > 0) {
      const ttlMs = config.sessionTtlMinutes * 60 * 1000
      const base = Math.max(stored.lastUserAt ?? 0, stored.lastBotAt ?? 0)
      if (base && Date.now() - base > ttlMs) return []
    }

    return (stored.history ?? []).slice(-config.maxMessages)
  })

  const [isOpen, setIsOpen] = useState(config.openByDefault)
  const [isTyping, setIsTyping] = useState(false)

  const sessionIdRef = useRef(sessionId)
  const messagesRef = useRef(messages)
  const consentGrantedRef = useRef(consentGranted)
  sessionIdRef.current = sessionId
  messagesRef.current = messages
  consentGrantedRef.current = consentGranted

  // Persist state whenever messages or sessionId changes
  const persist = useCallback(
    (msgs: Message[], sid: string) => {
      const lastBotAt = msgs.filter((m) => m.role === 'bot').at(-1)?.timestamp ?? 0
      const lastUserAt = msgs.filter((m) => m.role === 'user').at(-1)?.timestamp ?? 0
      saveState(config.storageKey, { sessionId: sid, history: msgs, lastBotAt, lastUserAt })
    },
    [config.storageKey]
  )

  const addMessage = useCallback(
    (role: 'user' | 'bot', content: { text?: string; html?: string; timestamp?: number }) => {
      const { timestamp: ts, ...rest } = content
      const msg: Message = { id: msgId(), role, timestamp: ts ?? Date.now(), ...rest }
      setMessages((prev) => {
        const next = [...prev, msg].slice(-config.maxMessages)
        persist(next, sessionIdRef.current)
        config.onEvent?.('message', { role, entry: msg })
        return next
      })
    },
    [config, persist]
  )

  // Add welcome message if history is empty. Deferred until consent is granted
  // when a privacy gate is active, so nothing renders/persists beforehand.
  const initializedRef = useRef(false)
  useEffect(() => {
    if (consentRequired && !consentGranted) return
    if (initializedRef.current) return
    initializedRef.current = true

    const locale = detectLocale(config.locale)
    const welcome = resolveWelcomeMessage(locale, config.welcomeMessages, config.welcomeMessage ?? uiTexts.welcome)
    if (messagesRef.current.length === 0 && welcome) {
      addMessage('bot', { text: welcome })
    }
    config.onEvent?.('ready', { sessionId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentGranted])

  // Session TTL interval check
  useEffect(() => {
    if (!config.sessionTtlMinutes || config.sessionTtlMinutes <= 0) return

    const ttlMs = config.sessionTtlMinutes * 60 * 1000
    const intervalMs = Math.min(Math.max(15000, Math.floor(ttlMs / 3)), 60000)

    const timer = setInterval(() => {
      const msgs = messagesRef.current
      const lastBotAt = msgs.filter((m) => m.role === 'bot').at(-1)?.timestamp ?? 0
      const lastUserAt = msgs.filter((m) => m.role === 'user').at(-1)?.timestamp ?? 0
      const base = Math.max(lastUserAt, lastBotAt)
      if (base && Date.now() - base > ttlMs) {
        const newSid = generateSessionId()
        setSessionId(newSid)
        setMessages([])
        persist([], newSid)
        setIsTyping(false)
        config.onEvent?.('sessionExpired', { sessionId: newSid })
        setMessages([{ id: msgId(), role: 'bot', timestamp: Date.now(), text: resolveUiTexts(config).sessionExpired }])
      }
    }, intervalMs)

    return () => clearInterval(timer)
  }, [config, persist])

  const buildPayload = useCallback(
    (text: string) => {
      const ctx = {
        sessionId: sessionIdRef.current,
        history: messagesRef.current.slice(-20),
        metadata: config.extraContext ?? {},
      }
      if (typeof config.transformRequest === 'function') {
        return config.transformRequest(text, ctx)
      }
      // Chat Trigger node expects 'chatInput', regular Webhook node expects 'message'
      const locale = detectLocale(config.locale)
      return { chatInput: text, sessionId: sessionIdRef.current, locale: locale || undefined, metadata: config.extraContext ?? {} }
    },
    [config]
  )

  const sendMessage = useCallback(
    async (text: string) => {
      // Hard guard: never reach the webhook before consent is given.
      if (consentRequired && !consentGrantedRef.current) return

      addMessage('user', { text })
      setIsTyping(true)

      const errorText = resolveWelcomeMessage(detectLocale(config.locale), config.errorMessages, uiTexts.error)

      try {
        const payload = buildPayload(text)
        const resp = await fetch(config.webhookUrl, {
          method: config.method,
          headers: { 'Content-Type': 'application/json', ...config.headers },
          body: config.method?.toUpperCase() === 'GET' ? undefined : JSON.stringify(payload),
          credentials: 'omit',
          mode: 'cors',
        })

        let data: unknown
        const contentType = resp.headers.get('content-type') ?? ''
        if (contentType.includes('application/json')) {
          data = await resp.json()
        } else {
          data = { reply: await resp.text() }
        }

        // Non-2xx (workflow error, proxy error, rate limit): never show the raw
        // server text. Only an explicit `output` field (e.g. a 429 from a
        // rate-limit branch) is shown; everything else gets the localized error.
        if (!resp.ok) {
          const errData = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null | undefined
          const output = errData?.output
          setIsTyping(false)
          if (typeof output === 'string' && output.trim()) {
            if (config.allowMarkdownInResponses) {
              addMessage('bot', { html: renderMarkdown(output) })
            } else if (config.allowHTMLInResponses && /<[^>]+>/.test(output)) {
              addMessage('bot', { html: sanitizeHtml(output) })
            } else {
              addMessage('bot', { text: output })
            }
          } else {
            addMessage('bot', { text: errorText })
          }
          config.onEvent?.('error', { error: new Error(`HTTP ${resp.status}`), status: resp.status })
          return
        }

        let out: string | { text?: string; html?: string } | null = null
        let serverTimestamp: number | undefined

        if (typeof config.transformResponse === 'function') {
          out = config.transformResponse(data)
        } else {
          // Normalize: n8n Chat Trigger returns an array [{ output: "..." }]
          const rawData = Array.isArray(data) ? (data[0] ?? {}) : data
          const d = rawData as Record<string, unknown>
          const currentDate = d?.currentDate as string | undefined
          serverTimestamp = currentDate ? new Date(currentDate).getTime() : undefined
          const raw = (d?.reply ?? d?.message ?? d?.text ?? d?.output ?? '') as string
          if (config.allowMarkdownInResponses && typeof raw === 'string') {
            out = { html: renderMarkdown(raw) }
          } else if (config.allowHTMLInResponses && typeof raw === 'string' && /<[^>]+>/.test(raw)) {
            out = { html: sanitizeHtml(raw) }
          } else {
            out = { text: raw }
          }
        }

        setIsTyping(false)

        if (typeof out === 'string') {
          if (config.allowMarkdownInResponses) {
            addMessage('bot', { html: renderMarkdown(out), timestamp: serverTimestamp })
          } else if (config.allowHTMLInResponses && /<[^>]+>/.test(out)) {
            addMessage('bot', { html: sanitizeHtml(out), timestamp: serverTimestamp })
          } else {
            addMessage('bot', { text: out, timestamp: serverTimestamp })
          }
        } else if (out && typeof out === 'object') {
          const { text, html } = out
          if (html && (config.allowMarkdownInResponses || config.allowHTMLInResponses)) {
            addMessage('bot', { html: config.allowMarkdownInResponses ? renderMarkdown(html) : sanitizeHtml(html), timestamp: serverTimestamp })
          } else if (text) {
            if (config.allowMarkdownInResponses) {
              addMessage('bot', { html: renderMarkdown(text), timestamp: serverTimestamp })
            } else {
              addMessage('bot', { text, timestamp: serverTimestamp })
            }
          } else {
            addMessage('bot', { text: '', timestamp: serverTimestamp })
          }
        } else {
          addMessage('bot', { text: uiTexts.emptyResponse })
        }
      } catch (err) {
        setIsTyping(false)
        addMessage('bot', { text: errorText })
        config.onEvent?.('error', { error: err })
      }
    },
    [addMessage, buildPayload, config, consentRequired]
  )

  // --- Consent lifecycle ---
  const privacyTexts = resolvePrivacyTexts(detectLocale(config.locale), config)

  const grantConsent = useCallback(() => {
    if (consentGrantedRef.current) return
    if (rememberConsent) saveConsent(consentStorageKey, privacy?.consentVersion)
    setConsentGranted(true)
    config.onEvent?.('consentGranted', { sessionId: sessionIdRef.current })
  }, [config, rememberConsent, consentStorageKey, privacy?.consentVersion])

  // Withdraw consent (DSGVO Art. 7(3)): clears the consent flag and all stored
  // chat data, then re-arms the gate. As easy to withdraw as it was to give.
  const revokeConsent = useCallback(() => {
    if (!consentRequired) return
    clearConsent(consentStorageKey)
    try {
      localStorage.removeItem(config.storageKey)
    } catch {
      // ignore
    }
    initializedRef.current = false
    setMessages([])
    setSessionId(generateSessionId())
    setIsTyping(false)
    setConsentGranted(false)
    config.onEvent?.('consentRevoked', {})
  }, [config, consentRequired, consentStorageKey])

  const open = useCallback(() => {
    setIsOpen(true)
    config.onEvent?.('toggle', { open: true })
  }, [config])

  const close = useCallback(() => {
    setIsOpen(false)
    config.onEvent?.('toggle', { open: false })
  }, [config])

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      config.onEvent?.('toggle', { open: !prev })
      return !prev
    })
  }, [config])

  const clear = useCallback(() => {
    setMessages([])
    persist([], sessionIdRef.current)
  }, [persist])

  return {
    config,
    uiTexts,
    messages,
    isOpen,
    isTyping,
    sessionId,
    sendMessage,
    open,
    close,
    toggle,
    clear,
    // Privacy gate
    consentRequired,
    consentGranted,
    privacyTexts,
    grantConsent,
    revokeConsent,
  }
}
